import React from "react";
import { ReactComponent as LighthouseAdded } from "./lighthouseAdded.svg";
import { ReactComponent as LighthouseDefault } from "./lighthouseDefault.svg";
import { ReactComponent as LighthouseMandatory } from "./lighthouseMandatory.svg";
import { ReactComponent as LighthouseOff } from "./lighthouseOff.svg";
import styles from "./LighthouseToggle.module.css";

/** What the lit lamp signals: why the form is on. */
export type LighthouseTone = "mandatory" | "default" | "added";

const LIT_ICONS: Record<LighthouseTone, typeof LighthouseOff> = {
  mandatory: LighthouseMandatory,
  default: LighthouseDefault,
  added: LighthouseAdded,
};

export const LighthouseIcon: React.FC<{
  on: boolean;
  tone: LighthouseTone;
  size?: number;
}> = ({ on, tone, size = 22 }) => {
  const Icon = on ? LIT_ICONS[tone] : LighthouseOff;
  return (
    <Icon
      width={size}
      height={size}
      aria-hidden="true"
      className={`${styles.icon} ${on ? "" : styles.off}`}
    />
  );
};

interface LighthouseToggleProps {
  on: boolean;
  tone: LighthouseTone;
  /** Accessible name, e.g. the form number and name. */
  label: string;
  /** Locked toggles render the state but do not respond to clicks. */
  locked: boolean;
  lockedReason?: string;
  onToggle: () => void;
}

/** A switch drawn as a lighthouse: selecting the form lights the lamp. */
export const LighthouseToggle: React.FC<LighthouseToggleProps> = ({
  on,
  tone,
  label,
  locked,
  lockedReason,
  onToggle,
}) => (
  <button
    type="button"
    role="switch"
    aria-checked={on}
    aria-label={label}
    disabled={locked}
    title={locked ? lockedReason : on ? "Selected" : "Not selected"}
    className={`${styles.toggle} ${locked ? styles.locked : ""}`}
    onClick={onToggle}
  >
    <LighthouseIcon on={on} tone={tone} />
    {locked && <i className={`pi pi-lock ${styles.lock}`} />}
  </button>
);
